import { useEffect, useRef } from 'react';
import { useAdvisor } from '@/components/feature/AdvisorProvider';
import { trackContact } from '@/hooks/usePixel';

export default function HeroSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const { wapp } = useAdvisor();

  useEffect(() => {
    const handleScroll = () => {
      if (imageRef.current) {
        const scrollY = window.scrollY;
        const parallaxValue = scrollY * 0.3;
        imageRef.current.style.transform = `translateY(${parallaxValue}px)`;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToServices = () => {
    const el = document.querySelector('#servicios');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      ref={sectionRef}
      className="relative min-h-screen w-full overflow-hidden bg-aa-black flex flex-col"
    >
      {/* Gold radial glow behind text */}
      <div className="absolute inset-0 hero-gold-glow pointer-events-none" />

      {/* Background image with parallax */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          ref={imageRef}
          className="absolute inset-0 w-full h-[120%] -top-[10%]"
        >
          <img
            src="https://readdy.ai/api/search-image?query=Panoramic%20skyline%20of%20Bogota%20Colombia%20financial%20district%20at%20dusk%2C%20modern%20skyscrapers%20with%20warm%20golden%20lights%20reflecting%20on%20glass%20buildings%2C%20dark%20atmospheric%20sky%2C%20professional%20corporate%20aesthetic%2C%20high-end%20editorial%20photography%2C%20dramatic%20lighting%2C%20no%20text&width=1920&height=1080&seq=hero-bg-01&orientation=landscape"
            alt="Skyline Latinoamericano"
            className="w-full h-full object-cover object-top"
          />
        </div>
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-aa-black/80 via-aa-black/60 to-aa-black/90" />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center flex-1 px-4 md:px-8 pt-24 pb-8">
        <div className="text-center max-w-5xl mx-auto">
          {/* Label */}
          <div className="label-tag mb-6 animate-fade-in-up">
            FINANCIAMIENTO PRIVADO ESTRUCTURADO
          </div>

          {/* Main Title */}
          <h1 className="heading-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 animate-fade-in-up stagger-1">
            Financiamos tu
            <br />
            <span className="gold-text-gradient">Proyecto</span>
            <br />
            desde 250.000 USD
          </h1>

          {/* Subtitle */}
          <p className="text-aa-gray-100 text-base md:text-lg lg:text-xl max-w-2xl mx-auto mb-10 leading-relaxed animate-fade-in-up stagger-2">
            Estructuramos soluciones financieras de escala institucional para empresarios, fondos de inversión y desarrolladores de proyectos en toda la región andina.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center gap-4 justify-center animate-fade-in-up stagger-3">
            <a
              href={wapp}
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackContact}
              className="btn-primary gold-shine text-sm md:text-base"
            >
              DESCUBRE NUESTROS SERVICIOS
            </a>
            <a
              href={wapp}
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackContact}
              className="btn-outline text-sm md:text-base flex items-center gap-2"
            >
              <i className="ri-whatsapp-line" />
              HABLEMOS POR WHATSAPP
            </a>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-fade-in-up stagger-5">
          <span className="text-aa-gray-400 text-xs uppercase tracking-widest">Scroll</span>
          <div className="w-5 h-8 rounded-full border border-aa-gold/30 flex items-start justify-center p-1">
            <div className="w-1 h-2 bg-aa-gold rounded-full animate-bounce" />
          </div>
        </div>
      </div>
    </section>
  );
}